export function googleStateCookieDomainForHost(hostname: string): string | undefined {
  const host = hostname.trim().toLowerCase();
  if (host === "lexicompanion.com" || host.endsWith(".lexicompanion.com")) {
    return "lexicompanion.com";
  }
  return undefined;
}

export function generatedGoogleCsrfToken(): string {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID().replace(/-/g, "");
  }
  const bytes = new Uint8Array(16);
  if (cryptoApi?.getRandomValues) {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

// TODO(auth-google-gis-diagnostics): remove this note with the temporary GIS diagnostics.
// GIS browser callbacks may omit g_csrf_token. For Lexi's backend double-submit
// CSRF check, the frontend may mint a temporary token, mirror it to the shared
// .lexicompanion.com cookie scope, and include the same value in the JSON body.
export function googleCsrfTokenForLogin({
  cookieToken,
  responseToken,
  fallbackToken = generatedGoogleCsrfToken(),
}: {
  cookieToken?: string;
  responseToken?: string;
  fallbackToken?: string;
}): string {
  return (cookieToken || "").trim() || (responseToken || "").trim() || fallbackToken.trim();
}

type DiagnosticValue = string | number | boolean | null | undefined;
type DiagnosticFields = Record<string, DiagnosticValue>;

const SAFE_DIAGNOSTIC_KEYS = new Set([
  "backend_status",
  "button_present",
  "client_id_len",
  "client_id_present",
  "credential_len",
  "credential_present",
  "error_class",
  "g_csrf_token_cookie_len",
  "g_csrf_token_cookie_present",
  "g_csrf_token_response_len",
  "g_csrf_token_response_present",
  "google_api_present",
  "google_button_present",
  "is_dismissed_moment",
  "is_display_moment",
  "is_not_displayed_moment",
  "is_skipped_moment",
  "provider_enabled",
  "provider_loading",
  "reason",
  "script_existing",
  "state_cookie_domain",
]);

const SENSITIVE_DIAGNOSTIC_KEY_PATTERN = /(credential|csrf|cookie|token|secret|sub|email|account|identifier)/i;
const SECRETISH_VALUE_PATTERN = /\b[A-Za-z0-9_./+=-]{24,}\b/;
const DIAGNOSTIC_QUERY_PARAMS = ["lexi_google_auth_diag", "lexiAuthDiag"];
const DIAGNOSTIC_STORAGE_KEYS = ["lexi.googleAuthDiag", "lexiGoogleAuthDiag"];

function sanitizeDiagnosticFields(fields: DiagnosticFields): DiagnosticFields {
  const safe: DiagnosticFields = {};
  for (const [key, value] of Object.entries(fields)) {
    if (SAFE_DIAGNOSTIC_KEYS.has(key)) {
      safe[key] = value;
      continue;
    }
    if (SENSITIVE_DIAGNOSTIC_KEY_PATTERN.test(key)) {
      safe[key] = "[redacted]";
      continue;
    }
    if (typeof value === "string" && SECRETISH_VALUE_PATTERN.test(value)) {
      safe[key] = "[redacted]";
      continue;
    }
    safe[key] = value;
  }
  return safe;
}

export function googleAuthDiagnosticsEnabled(): boolean {
  if (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") {
    return true;
  }
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of DIAGNOSTIC_QUERY_PARAMS) {
      const value = params.get(key);
      if (value === "1" || value === "true") return true;
    }
  } catch {
    // ignore malformed location objects
  }
  try {
    for (const key of DIAGNOSTIC_STORAGE_KEYS) {
      const value = window.localStorage?.getItem(key);
      if (value === "1" || value === "true") return true;
    }
  } catch {
    // localStorage may be unavailable in restricted contexts
  }
  return false;
}

// TODO(auth-google-gis-diagnostics): remove after the real GIS browser callback path is classified.
export function logGoogleAuthDiagnostic(event: string, fields: DiagnosticFields = {}): void {
  if (typeof console === "undefined" || typeof console.info !== "function") return;
  if (!googleAuthDiagnosticsEnabled()) return;
  console.info("[LexiGoogleAuthDiag]", event, sanitizeDiagnosticFields(fields));
}
